import { LOCALE_NAMES } from "@repo/shared";
import type { CloneJob, LocaleCode } from "@repo/shared/types";
import { AlertCircle, Languages } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { TableCell, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api/_client";
import { useDismissCloneJob } from "@/lib/hooks/use-clone-jobs";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./clone-job-row.i18n";

type CloneJobRowProps = {
  job: CloneJob;
  /** The table's column count: the row spans all of them. */
  colSpan: number;
};

/**
 * QL-334 — a translated copy that is still being made, shown at the top of
 * the flows or PDF templates list until the real row replaces it.
 */
export function CloneJobRow({ job, colSpan }: CloneJobRowProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  const dismiss = useDismissCloneJob();
  const language = LOCALE_NAMES[job.language as LocaleCode] || job.language;
  const failed = job.status === "failed";
  const saving = job.status === "processing" && job.translatedTexts >= job.totalTexts;
  const percent = job.totalTexts > 0 ? (job.translatedTexts / job.totalTexts) * 100 : 0;
  const count = (n: number) => formatNumber(n, { maximumFractionDigits: 0 });

  const status = failed
    ? (job.error ?? t.failed)
    : job.status === "pending"
      ? t.waiting(language)
      : saving
        ? t.saving
        : t.translating(language, count(job.translatedTexts), count(job.totalTexts));

  return (
    <TableRow className="bg-muted/40 hover:bg-muted/40 cursor-default!" aria-busy={!failed}>
      <TableCell colSpan={colSpan}>
        <div className="flex items-center gap-4">
          {failed ? (
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
          ) : (
            <Languages className="h-4 w-4 shrink-0 text-muted-foreground animate-pulse" />
          )}
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{job.name}</div>
            <p className={failed ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
              {status}
            </p>
          </div>
          {!failed && (
            <Progress
              value={saving ? 100 : percent}
              className="w-40 shrink-0"
              aria-label={status}
            />
          )}
          {(failed || job.status === "pending") && job.mine && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                dismiss.mutate(job.id, {
                  // e.g. the copy started just before Cancel: the server says so.
                  onError: (err) => toast.error(apiErrorMessage(err, t.removeFailed)),
                })
              }
              loading={dismiss.isPending}
            >
              {failed ? t.dismiss : t.cancel}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
